/**
 * Histórico de edições no painel (localStorage).
 * Preparado para futura delegação a API de auditoria.
 */

export type PainelActivityAction =
  | 'create'
  | 'update'
  | 'delete'
  | 'publish'
  | 'submit_review'
  | 'approve';

export type PainelActivityResource = 'guide' | 'power' | 'ace';

export type PainelPublicationStatus = 'draft' | 'pending_review' | 'published';

export type PainelActivityEntry = {
  id: string;
  username: string;
  userName: string;
  action: PainelActivityAction;
  resource: PainelActivityResource;
  resourceId: string;
  resourceLabel: string;
  timestamp: string;
};

export type PainelCollaborationStats = {
  guides: number;
  powers: number;
  aces: number;
  total: number;
};

const STORAGE_KEY = 'painel_activity_log_v1';
const MAX_ENTRIES = 800;

const ACTION_LABELS: Record<PainelActivityAction, string> = {
  create: 'Criou',
  update: 'Editou',
  delete: 'Excluiu',
  publish: 'Publicou',
  submit_review: 'Enviou para revisão',
  approve: 'Aprovou',
};

const RESOURCE_LABELS: Record<PainelActivityResource, string> = {
  guide: 'guia',
  power: 'power',
  ace: 'ace',
};

function loadLog(): PainelActivityEntry[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as PainelActivityEntry[]) : [];
  } catch {
    return [];
  }
}

function saveLog(entries: PainelActivityEntry[]): void {
  if (typeof window === 'undefined') return;
  const trimmed = entries.slice(0, MAX_ENTRIES);
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
}

function seedDemoActivityIfEmpty(): void {
  if (typeof window === 'undefined') return;
  if (loadLog().length > 0) return;

  const now = Date.now();
  const day = 86_400_000;
  const samples: Omit<PainelActivityEntry, 'id' | 'timestamp'>[] = [
    {
      username: 'admin',
      userName: 'Admin',
      action: 'update',
      resource: 'guide',
      resourceId: 'g-conta',
      resourceLabel: 'Criar uma conta no xat',
    },
    {
      username: 'cupim',
      userName: 'Cupim',
      action: 'create',
      resource: 'power',
      resourceId: 'maus',
      resourceLabel: 'Maus',
    },
    {
      username: 'guinho',
      userName: 'Guinho',
      action: 'update',
      resource: 'ace',
      resourceId: 'shortname',
      resourceLabel: 'Shortname',
    },
    {
      username: 'paulo',
      userName: 'Paulo',
      action: 'approve',
      resource: 'guide',
      resourceId: 'g-powers',
      resourceLabel: 'Como usar powers',
    },
    {
      username: 'editor',
      userName: 'Editor',
      action: 'submit_review',
      resource: 'guide',
      resourceId: 'g-novo',
      resourceLabel: 'Primeiros passos no chat',
    },
    {
      username: 'thuk',
      userName: 'Thuk',
      action: 'update',
      resource: 'power',
      resourceId: 'everypower',
      resourceLabel: 'Everypower',
    },
  ];

  const seeded: PainelActivityEntry[] = samples.map((entry, index) => ({
    ...entry,
    id: `seed-${index}`,
    timestamp: new Date(now - (index + 2) * day * 1.4).toISOString(),
  }));

  saveLog(seeded);
}

export function recordPainelActivity(
  input: Omit<PainelActivityEntry, 'id' | 'timestamp'>
): PainelActivityEntry {
  seedDemoActivityIfEmpty();
  const entry: PainelActivityEntry = {
    ...input,
    id: `act-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    timestamp: new Date().toISOString(),
  };
  const next = [entry, ...loadLog()];
  saveLog(next);
  return entry;
}

export function getPainelActivityLog(options?: {
  username?: string;
  resource?: PainelActivityResource;
  limit?: number;
}): PainelActivityEntry[] {
  seedDemoActivityIfEmpty();
  let entries = loadLog();
  if (options?.username) {
    const user = options.username.trim().toLowerCase();
    entries = entries.filter((e) => e.username === user);
  }
  if (options?.resource) {
    entries = entries.filter((e) => e.resource === options.resource);
  }
  const limit = options?.limit ?? 50;
  return entries.slice(0, limit);
}

export function getResourceActivityLog(
  resource: PainelActivityResource,
  resourceId: string,
  options?: { limit?: number; order?: 'asc' | 'desc' }
): PainelActivityEntry[] {
  seedDemoActivityIfEmpty();
  const id = resourceId.trim();
  let entries = loadLog().filter((e) => e.resource === resource && e.resourceId === id);

  entries.sort((a, b) => {
    const diff = new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
    return options?.order === 'desc' ? -diff : diff;
  });

  const limit = options?.limit ?? MAX_ENTRIES;
  return entries.slice(0, limit);
}

export function getUserCollaborationStats(username: string): PainelCollaborationStats {
  const entries = getPainelActivityLog({ username, limit: MAX_ENTRIES }).filter(
    (e) => e.action !== 'delete'
  );
  const guides = entries.filter((e) => e.resource === 'guide').length;
  const powers = entries.filter((e) => e.resource === 'power').length;
  const aces = entries.filter((e) => e.resource === 'ace').length;
  return { guides, powers, aces, total: guides + powers + aces };
}

export function formatActivitySummary(entry: PainelActivityEntry): string {
  const action = ACTION_LABELS[entry.action];
  const resource = RESOURCE_LABELS[entry.resource];
  return `${action} ${resource} «${entry.resourceLabel}»`;
}

export function formatActivityAction(entry: PainelActivityEntry): string {
  return ACTION_LABELS[entry.action];
}

export function formatActivityTimestamp(iso: string): string {
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export type PainelActivityActor = {
  username: string;
  name: string;
};

export function logPainelContentActivity(
  actor: PainelActivityActor | null | undefined,
  input: {
    action: PainelActivityAction;
    resource: PainelActivityResource;
    resourceId: string;
    resourceLabel: string;
  }
): void {
  if (!actor?.username) return;
  recordPainelActivity({
    username: actor.username.trim().toLowerCase(),
    userName: actor.name.trim() || actor.username,
    ...input,
  });
}

/** Regista criação/edição/publicação e envio para revisão ao guardar no editor. */
export function logPainelEditorPublicationActivity(
  actor: PainelActivityActor | null | undefined,
  input: {
    resource: PainelActivityResource;
    resourceId: string;
    resourceLabel: string;
    isNew: boolean;
    previousStatus?: PainelPublicationStatus | null;
    nextStatus: PainelPublicationStatus;
  }
): void {
  const { resource, resourceId, resourceLabel, isNew, previousStatus, nextStatus } = input;
  const base = { resource, resourceId, resourceLabel };

  if (isNew) {
    logPainelContentActivity(actor, { ...base, action: 'create' });
  } else if (nextStatus === 'published' && previousStatus !== 'published') {
    logPainelContentActivity(actor, { ...base, action: 'publish' });
  } else if (!isNew) {
    logPainelContentActivity(actor, { ...base, action: 'update' });
  }

  if (nextStatus === 'pending_review' && previousStatus !== 'pending_review') {
    logPainelContentActivity(actor, { ...base, action: 'submit_review' });
  }
}

export function logPainelSubmitForReview(
  actor: PainelActivityActor | null | undefined,
  input: {
    resource: PainelActivityResource;
    resourceId: string;
    resourceLabel: string;
  }
): void {
  logPainelContentActivity(actor, { ...input, action: 'submit_review' });
}

export function logPainelApproveReview(
  actor: PainelActivityActor | null | undefined,
  input: {
    resource: PainelActivityResource;
    resourceId: string;
    resourceLabel: string;
  }
): void {
  logPainelContentActivity(actor, { ...input, action: 'approve' });
}
